import { useEffect, useState } from "react"
import styled from "styled-components"
import { toast } from "react-hot-toast"
import { Bell, X, Send } from "lucide-react"

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  @media (max-width: 900px) {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }
`

const Modal = styled.div`
  background: white;
  width: 100%;
  max-width: 460px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    max-width: 100vw;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  &:hover { background: #f1f5f9; color: #0f172a; }
`

const Body = styled.div`
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const MainSwitch = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${(p) => (p.on ? "#eff6ff" : "#f9fafb")};
  border: 1px solid ${(p) => (p.on ? "#bfdbfe" : "#e5e7eb")};
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  strong {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    display: block;
  }
  small {
    font-size: 12px;
    color: #64748b;
  }
`

const ToggleInput = styled.input.attrs({ type: "checkbox" })`
  width: 38px;
  height: 22px;
  appearance: none;
  background: #cbd5e1;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: 50%;
    transition: transform 0.15s;
  }

  &:checked { background: #2563eb; }
  &:checked::after { transform: translateX(16px); }
`

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
  text-transform: none;
`

const SlotRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  ${(p) => p.disabled && `opacity: 0.5; cursor: not-allowed;`}

  & + & { margin-top: 6px; }
`

const SlotInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
  }
  span {
    font-size: 11.5px;
    color: #64748b;
  }
`

const Footer = styled.div`
  padding: 12px 20px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const TextBtn = styled.button`
  border: none;
  background: transparent;
  color: #475569;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover { color: #2563eb; }
`

const Actions = styled.div`
  display: inline-flex;
  gap: 8px;
`

const PrimaryBtn = styled.button`
  padding: 8px 16px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  &:hover:not(:disabled) { background: #1d4ed8; }
  &:disabled { background: #cbd5e1; cursor: not-allowed; }
`

const GhostBtn = styled.button`
  padding: 8px 14px;
  background: white;
  color: #475569;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  &:hover { background: #f9fafb; }
`

const SLOTS = [
  { key: "slot_morning", label: "🌅 오전 08:00", desc: "오늘 진행할 작업 요약" },
  { key: "slot_noon",    label: "🍱 점심 12:00", desc: "마감 임박 + 지연 작업 알림" },
  { key: "slot_evening", label: "🌙 저녁 18:00", desc: "보고서 작성 리마인드" },
]

export default function AlarmSettingsModal({ isOpen, onClose }) {
  const [settings, setSettings] = useState({
    enabled: false, slot_morning: true, slot_noon: true, slot_evening: true,
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    fetch("/request/alarm/settings", { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (data.success) setSettings(data.settings)
        else toast.error(data.message || "설정 조회 실패")
      })
      .catch(() => toast.error("서버 연결 오류"))
      .finally(() => setLoading(false))
  }, [isOpen])

  const save = async () => {
    setSaving(true)
    try {
      const r = await fetch("/request/alarm/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings),
      })
      const data = await r.json()
      if (!data.success) throw new Error(data.message)
      toast.success("저장되었습니다")
      onClose()
    } catch (e) {
      toast.error(e.message || "저장 실패")
    } finally {
      setSaving(false)
    }
  }

  const test = async () => {
    setTesting(true)
    try {
      const r = await fetch("/request/alarm/test", {
        method: "POST",
        credentials: "include",
      })
      const data = await r.json()
      if (data.success) {
        toast.success("테스트 푸시 발송 — AntsTalk 알림 확인하세요")
      } else {
        toast.error(data.result?.message || data.message || "테스트 실패")
      }
    } catch (e) {
      toast.error("테스트 발송 오류")
    } finally {
      setTesting(false)
    }
  }

  if (!isOpen) return null
  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <Modal>
        <Header>
          <Title><Bell size={18} color="#2563eb" /> 알람 설정</Title>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </Header>
        <Body>
          <MainSwitch on={settings.enabled}>
            <div>
              <strong>앤츠톡 푸시 알람</strong>
              <small>등록된 모든 디바이스(폰/PC)로 푸시 발송</small>
            </div>
            <ToggleInput
              checked={settings.enabled}
              onChange={(e) => setSettings(s => ({ ...s, enabled: e.target.checked }))}
              disabled={loading}
            />
          </MainSwitch>

          <div>
            <SectionTitle>알람 시간 (각 시간대별 ON/OFF)</SectionTitle>
            {SLOTS.map(slot => (
              <SlotRow key={slot.key} disabled={!settings.enabled || loading}>
                <SlotInfo>
                  <strong>{slot.label}</strong>
                  <span>{slot.desc}</span>
                </SlotInfo>
                <ToggleInput
                  checked={!!settings[slot.key]}
                  onChange={(e) => setSettings(s => ({ ...s, [slot.key]: e.target.checked }))}
                  disabled={!settings.enabled || loading}
                />
              </SlotRow>
            ))}
          </div>
        </Body>

        <Footer>
          <TextBtn onClick={test} disabled={testing}>
            <Send size={13} /> {testing ? "발송 중…" : "테스트 발송"}
          </TextBtn>
          <Actions>
            <GhostBtn onClick={onClose}>취소</GhostBtn>
            <PrimaryBtn onClick={save} disabled={loading || saving}>
              {saving ? "저장 중…" : "저장"}
            </PrimaryBtn>
          </Actions>
        </Footer>
      </Modal>
    </Overlay>
  )
}
